import React, { useEffect, useMemo, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ALBERTA_RESTAURANTS, ALBERTA_FOOD_TRUCKS } from '../data/restaurants';
import { useAuth } from '../context/AuthContext';
import { apiFetch } from '../lib/api';
import { setMeta } from '../lib/seo';
import { getRestaurantImage } from '../lib/foodImages';
import { isTrending, isNew } from '../data/trending';
import Footer from '../components/Footer';
import NotFound from './NotFound';

const PRICE_COLORS = {
  $: 'bg-green-100 text-green-800',
  $$: 'bg-yellow-100 text-yellow-800',
  $$$: 'bg-orange-100 text-orange-800',
  $$$$: 'bg-red-100 text-red-800',
};

export default function RestaurantDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [isFav, setIsFav] = useState(false);

  const staticMatch = useMemo(
    () => [...ALBERTA_RESTAURANTS, ...ALBERTA_FOOD_TRUCKS].find((r) => r.id === id),
    [id]
  );
  const [apiMatch, setApiMatch] = useState(null);
  const [resolving, setResolving] = useState(false);

  // Fall back to the dynamic API for ids not in the static curated set
  // (e.g. scraped + admin-approved spots added without a redeploy).
  useEffect(() => {
    if (staticMatch) { setApiMatch(null); return; }
    setResolving(true);
    apiFetch(`/api/restaurants/${id}`)
      .then((r) => setApiMatch(r))
      .catch(() => setApiMatch(null))
      .finally(() => setResolving(false));
  }, [id, staticMatch]);

  const restaurant = staticMatch || apiMatch;

  useEffect(() => {
    if (!restaurant) return;
    setMeta({
      title: `${restaurant.name} — ${restaurant.city}, Alberta | Culinera`,
      description: `${restaurant.description} ${restaurant.cuisine} in ${restaurant.city}, Alberta. ${restaurant.price}. Curated on Culinera.`,
      canonical: `https://culinera.ca/restaurants/${restaurant.id}`,
      image: restaurant.image_url || undefined,
      jsonLd: {
        '@context': 'https://schema.org',
        '@type': 'Restaurant',
        name: restaurant.name,
        description: restaurant.description,
        address: {
          '@type': 'PostalAddress',
          addressLocality: restaurant.city,
          addressRegion: 'AB',
          addressCountry: 'CA',
        },
        servesCuisine: restaurant.cuisine,
        priceRange: restaurant.price,
      },
    });
  }, [restaurant]);

  useEffect(() => {
    if (!isAuthenticated || !restaurant) return;
    apiFetch('/api/favorites')
      .then((list) => setIsFav(!!(list || []).find((f) => f.restaurant_id === restaurant.id)))
      .catch(() => {});
  }, [isAuthenticated, restaurant]);

  if (!restaurant) {
    if (resolving) {
      return (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 to-cyan-50 flex items-center justify-center p-8">
          <div className="text-gray-500" data-testid="restaurant-loading">Loading…</div>
        </div>
      );
    }
    return <NotFound />;
  }

  const toggleFav = async () => {
    if (!isAuthenticated) { navigate('/?signin=1'); return; }
    if (isFav) {
      setIsFav(false);
      try { await apiFetch(`/api/favorites/${restaurant.id}`, { method: 'DELETE' }); } catch {}
    } else {
      setIsFav(true);
      try { await apiFetch('/api/favorites', { method: 'POST', body: { restaurant_id: restaurant.id } }); } catch {}
    }
  };

  const nearby = ALBERTA_RESTAURANTS
    .filter((r) => r.city === restaurant.city && r.id !== restaurant.id)
    .slice(0, 4);

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white/90 backdrop-blur sticky top-0 z-30 border-b border-gray-100">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-between items-center py-4">
          <Link to="/" className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-gradient-to-br from-teal-600 to-cyan-600 rounded-full flex items-center justify-center">
              <span className="text-white font-bold text-sm">C</span>
            </div>
            <span className="text-xl font-bold text-gray-900">Culinera</span>
          </Link>
          <Link to="/" className="text-teal-600 hover:text-teal-700 font-semibold">← Home</Link>
        </div>
      </header>

      {/* Hero banner */}
      <div className="relative w-full h-72 sm:h-96 overflow-hidden">
        <img
          src={getRestaurantImage(restaurant, 1600)}
          alt={`${restaurant.name} — ${restaurant.cuisine} in ${restaurant.city}, Alberta`}
          className="w-full h-full object-cover"
          data-testid="restaurant-detail-image"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-black/10" />
        <div className="absolute inset-x-0 bottom-0">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pb-6">
            <nav className="text-xs text-white/70 mb-3" aria-label="Breadcrumb">
              <Link to="/" className="hover:text-white">Home</Link> › <span>{restaurant.city}</span> › <span className="text-white">{restaurant.name}</span>
            </nav>
            <div className="flex items-end justify-between gap-4">
              <div>
                <div className="flex flex-wrap gap-2 mb-3">
                  <span className="text-xs font-bold bg-white/95 text-gray-900 px-3 py-1 rounded-full">{restaurant.price}</span>
                  <span className="text-xs font-semibold bg-white/20 backdrop-blur text-white px-3 py-1 rounded-full border border-white/30">{restaurant.cuisine}</span>
                  <span className="text-xs font-semibold bg-teal-500 text-white px-3 py-1 rounded-full">★ Culinera Pick</span>
                  {isNew(restaurant.id) && (
                    <span data-testid="detail-new-badge" className="text-xs font-extrabold bg-rose-600 text-white px-3 py-1 rounded-full tracking-wide">● NEW</span>
                  )}
                  {isTrending(restaurant.id) && (
                    <span data-testid="detail-trending-badge" className="text-xs font-bold bg-amber-400 text-amber-950 px-3 py-1 rounded-full">✦ Trending this month</span>
                  )}
                </div>
                <h1 className="text-3xl sm:text-5xl font-bold text-white leading-tight drop-shadow-sm">{restaurant.name}</h1>
                <p className="text-white/80 mt-2 text-sm sm:text-base">📍 {restaurant.city}{restaurant.neighborhood ? `, ${restaurant.neighborhood}` : ''}, Alberta</p>
              </div>
              <button
                data-testid={`fav-btn-${restaurant.id}`}
                onClick={toggleFav}
                className={`shrink-0 w-12 h-12 rounded-full bg-white shadow-lg flex items-center justify-center text-2xl transition-all hover:scale-110 ${isFav ? 'text-red-500' : 'text-gray-400 hover:text-red-400'}`}
                aria-label={isFav ? 'Remove from favorites' : 'Add to favorites'}
              >{isFav ? '♥' : '♡'}</button>
            </div>
          </div>
        </div>
      </div>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 bg-white rounded-2xl shadow-sm p-6 sm:p-8">
            <h2 className="text-lg font-bold text-gray-900 mb-3">About {restaurant.name}</h2>
            <p className="text-gray-700 text-lg leading-relaxed mb-6">{restaurant.description}</p>

            {(restaurant.tags || []).length > 0 && (
              <div className="flex flex-wrap gap-2 mb-6">
                {(restaurant.tags || []).map((t) => (
                  <span key={t} className="text-xs font-medium bg-teal-50 text-teal-700 px-3 py-1.5 rounded-full">{t}</span>
                ))}
              </div>
            )}

            {(restaurant.website || restaurant.instagram || restaurant.twitter) && (
              <div className="flex flex-wrap gap-3 pt-5 border-t border-gray-100">
                {restaurant.website && (
                  <a href={restaurant.website} target="_blank" rel="noopener noreferrer" data-testid="restaurant-website" className="inline-flex items-center gap-2 text-sm font-semibold text-teal-700 bg-teal-50 hover:bg-teal-100 px-4 py-2 rounded-full transition-colors">🌐 Visit Website</a>
                )}
                {restaurant.instagram && (
                  <a href={`https://instagram.com/${restaurant.instagram}`} target="_blank" rel="noopener noreferrer" data-testid="restaurant-instagram" className="inline-flex items-center gap-2 text-sm font-semibold text-pink-600 bg-pink-50 hover:bg-pink-100 px-4 py-2 rounded-full transition-colors">📷 @{restaurant.instagram}</a>
                )}
                {restaurant.twitter && (
                  <a href={`https://twitter.com/${restaurant.twitter}`} target="_blank" rel="noopener noreferrer" data-testid="restaurant-twitter" className="inline-flex items-center gap-2 text-sm font-semibold text-gray-800 bg-gray-100 hover:bg-gray-200 px-4 py-2 rounded-full transition-colors">𝕏 @{restaurant.twitter}</a>
                )}
              </div>
            )}
          </div>

          <aside className="bg-white rounded-2xl shadow-sm p-6 h-fit">
            <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wide mb-4">At a glance</h3>
            <dl className="space-y-3 text-sm">
              {[['Cuisine', restaurant.cuisine], ['Price', restaurant.price], ['City', restaurant.city], ['Area', restaurant.neighborhood]]
                .filter(([, v]) => typeof v === 'string' ? v.trim() : v)
                .map(([label, v]) => (
                  <div key={label} data-testid={`glance-${label.toLowerCase()}`} className="flex justify-between"><dt className="text-gray-500">{label}</dt><dd className="font-semibold text-gray-900">{v}</dd></div>
                ))}
            </dl>
            <Link to="/" className="mt-6 block text-center bg-teal-600 text-white font-semibold px-4 py-3 rounded-xl hover:bg-teal-700 transition-colors">Plan a trip with this spot</Link>
          </aside>
        </div>

        {nearby.length > 0 && (
          <section className="mt-8">
            <h2 className="text-xl font-bold text-gray-900 mb-4">More in {restaurant.city}</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {nearby.map((r) => (
                <Link
                  key={r.id}
                  to={`/restaurants/${r.id}`}
                  data-testid={`nearby-${r.id}`}
                  className="group bg-white rounded-2xl shadow-sm overflow-hidden hover:shadow-lg transition-all block"
                >
                  <div className="relative h-32 overflow-hidden">
                    <img src={getRestaurantImage(r, 500)} alt={r.name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" loading="lazy" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                    <span className="absolute top-2 left-2 text-xs font-bold bg-white/90 text-gray-900 px-2 py-0.5 rounded-full">{r.price}</span>
                  </div>
                  <div className="p-3">
                    <div className="font-semibold text-gray-900 text-sm leading-tight group-hover:text-teal-700 transition-colors">{r.name}</div>
                    <div className="text-xs text-gray-500 mt-1">{r.cuisine} · {r.neighborhood || r.city}</div>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}
      </main>
      <Footer />
    </div>
  );
}
